<script setup>
import { computed, inject } from 'vue'
import SzwImage from '@/__v1.5__/components/image/Image.vue'
import Sprite from '@/__core__/components/sprite/Sprite.vue'
import EventCardSkeleton from '@/__v1.5__/components/cards/EventCardSkeleton.vue'

/** @typedef {{ content: string, type: 'substitute-in' | 'substitute-out' | 'own-goal' | 'goal' | 'yellow-card' | 'red-card' }} Event */
const props = defineProps({
  logo: String,
  title: String,
  minute: String,
  subtitle: String,
  cursor: Boolean,
  spriteId: String,
  teamName: String,
  hasTeamLogo: Boolean
})

const status = inject('status')

const emit = defineEmits(['card-click'])

function emitCardClick() {
  emit('card-click')
}

const cardContentWidth = computed(() => (props.hasTeamLogo ? 'calc(100% - 100px)' : 'calc(100% - 75px)'))
</script>

<template>
  <event-card-skeleton v-if="status === 'loading'" :has-team-logo="hasTeamLogo" />

  <div
    v-else
    :class="['szw-flex szw-w-full szw-h-[64px] szw-p-[3px] szw-gap-[3px] szw-rounded-2 szw-items-center', { 'szw-cursor-pointer': cursor }]"
    style="border: 1px solid var(--szw-border)"
    @click="emitCardClick"
  >
    <div class="szw-w-[70px] szw-h-[56px] szw-relative szw-rounded szw-overflow-hidden">
      <div class="skewed-div" />

      <div class="szw-absolute szw-z-50 szw-left-0 szw-top-0 szw-w-[52px] szw-h-full szw-flex szw-col szw-justify-center szw-items-center szw-overflow-hidden">
        <sprite :sprite-id="spriteId" />
        <span class="szw-text-font-size-xs szw-leading-font-line-height-xs">{{ minute }}</span>
      </div>
    </div>

    <div class="szw-flex szw-flex-col szw-justify-center szw-gap-1 szw-mr-auto" :style="{ width: cardContentWidth }">
      <span class="szw-text-font-size-md szw-font-bold szw-leading-font-line-height-md szw-tracking-[0%]">{{ title }}</span>
      <span v-show="subtitle" class="szw-text-font-size-sm szw-leading-font-line-height-sm szw-tracking-[0%]">{{ subtitle }}</span>
    </div>

    <div v-if="hasTeamLogo" class="szw-mr-[5px]">
      <szw-image :src="logo" class="szw-h-[20px] szw-w-[20px]" alt="team-logo" :fallback-content="teamName" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.skewed-div {
  @apply szw-absolute szw-z-0 -szw-left-[20px] szw-top-0;

  width: 70px;
  height: 56px;
  margin: 0;
  border-radius: 4px;
  overflow: hidden;
  transform: skewX(-20deg);
  transform-origin: 0 100%;

  background: var(--szw-background-color-secondary);
}
</style>
